import { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { comics } from '@/mocks/comics';

const categoryPageSeoData: Record<string, { title: string; description: string; keywords: string }> = {
  romance: {
    title: '恋爱漫画 - 韩国恋爱漫画免费在线阅读 - 韩国漫画网',
    description: '韩国漫画网恋爱漫画分类，收录超过3500部韩国恋爱漫画，包含校园恋爱、职场恋爱、宫廷恋爱等类型，免费在线阅读，每日更新。',
    keywords: '恋爱漫画,韩国恋爱漫画,恋爱韩漫,免费恋爱漫画,浪漫韩漫',
  },
  fantasy: {
    title: '奇幻冒险漫画 - 韩国奇幻漫画免费在线阅读 - 韩国漫画网',
    description: '韩国漫画网奇幻冒险分类，收录超过2800部韩国奇幻漫画，包含异世界穿越、魔法战斗、龙与地下城等，免费在线阅读。',
    keywords: '奇幻漫画,冒险漫画,韩国奇幻漫画,异世界韩漫,魔法漫画',
  },
  action: {
    title: '动作热血漫画 - 韩国动作漫画免费在线阅读 - 韩国漫画网',
    description: '韩国漫画网动作热血分类，收录超过2100部韩国动作漫画，包含格斗竞技、异能战斗、热血冒险等，免费在线阅读。',
    keywords: '动作漫画,热血漫画,韩国动作漫画,格斗韩漫,战斗漫画',
  },
  mystery: {
    title: '悬疑推理漫画 - 韩国悬疑漫画免费在线阅读 - 韩国漫画网',
    description: '韩国漫画网悬疑推理分类，收录超过1600部韩国悬疑漫画，包含侦探推理、恐怖惊悚、心理悬疑等，免费在线阅读。',
    keywords: '悬疑漫画,推理漫画,韩国悬疑漫画,恐怖韩漫,侦探漫画',
  },
  drama: {
    title: '剧情生活漫画 - 韩国剧情漫画免费在线阅读 - 韩国漫画网',
    description: '韩国漫画网剧情生活分类，收录超过1900部韩国剧情漫画，包含职场生活、家庭伦理、青春成长等现实主义题材，免费阅读。',
    keywords: '剧情漫画,生活漫画,韩国剧情漫画,现实主义韩漫,职场漫画',
  },
  comedy: {
    title: '搞笑日常漫画 - 韩国搞笑漫画免费在线阅读 - 韩国漫画网',
    description: '韩国漫画网搞笑日常分类，收录超过2300部韩国搞笑漫画，包含轻松搞笑、日常喜剧、吐槽幽默等，免费在线阅读。',
    keywords: '搞笑漫画,日常漫画,韩国搞笑漫画,爆笑韩漫,喜剧漫画',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  const categoryComics = useMemo(() => {
    if (!category) return [];
    const filtered = comics.filter(c => c.categories.some(cat => category.name.includes(cat) || cat.includes(category.name.replace('漫画', ''))));
    switch (sortBy) {
      case 'latest':
        return [...filtered].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
      case 'rating':
        return [...filtered].sort((a, b) => b.rating - a.rating);
      default:
        return [...filtered].sort((a, b) => b.views - a.views);
    }
  }, [category, sortBy]);

  useEffect(() => {
    if (category && categoryPageSeoData[slug || '']) {
      document.title = categoryPageSeoData[slug || ''].title;
    } else {
      document.title = '漫画分类 - 韩国漫画网';
    }
  }, [slug, category]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">分类未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的分类不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-medium cursor-pointer hover:bg-primary-600 transition-colors">
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">{category.name}</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
            <div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-2">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-500">{category.description} · 共 {category.count.toLocaleString()} 部作品</p>
            </div>
            <div className="flex items-center gap-2">
              {(['popular', 'latest', 'rating'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setSortBy(s)}
                  className={`px-4 py-2 rounded-full text-xs font-label cursor-pointer transition-colors whitespace-nowrap ${
                    sortBy === s
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {s === 'popular' && '最受欢迎'}
                  {s === 'latest' && '最新更新'}
                  {s === 'rating' && '评分最高'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6 pb-16" data-product-shop="">
            {categoryComics.map((comic) => (
              <Link key={comic.id} to={`/comic/${comic.id}`} className="group cursor-pointer">
                <div className="relative rounded-xl overflow-hidden aspect-[3/4] mb-3">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - ${category.name}`}
                    title={`${comic.title} 韩国漫画`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  {comic.status === '已完结' && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-foreground-950/70 text-background-50 text-xs font-label">
                      已完结
                    </div>
                  )}
                </div>
                <h3 className="text-sm md:text-base font-heading font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                  {comic.title}
                </h3>
                <p className="text-xs text-foreground-500">{comic.author}</p>
              </Link>
            ))}
          </div>

          {categoryComics.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${category.name} - 韩国漫画网`,
          "description": category.description,
          "url": `http://szfirstteam.com/category/${slug}`,
          "isPartOf": {
            "@type": "WebSite",
            "name": "韩国漫画网",
            "url": "http://szfirstteam.com/"
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://szfirstteam.com/" },
              { "@type": "ListItem", "position": 2, "name": category.name, "item": `http://szfirstteam.com/category/${slug}` }
            ]
          }
        })}
      </script>
    </>
  );
}